import api from '../../../shared/services/api';
import { API_ENDPOINTS } from '../../../shared/constants/apiEndpoints';

/**
 * Service for managing FMO specification data (clusterspecs, pocketspecs, inputspecs)
 */
export const fmoSpecService = {
  /**
   * Fetch all specification types in parallel
   * @returns {Promise<Object>} Object containing clusterspecs, pocketspecs, and inputspecs arrays
   */
  async fetchAllSpecs() {
    try {
      const [clusterspecsRes, pocketspecsRes, inputspecsRes] = await Promise.all([
        api.get(API_ENDPOINTS.CLUSTERSPECS.BASE),
        api.get(API_ENDPOINTS.POCKETSPECS.BASE),
        api.get(API_ENDPOINTS.INPUTSPECS.BASE)
      ]);
      
      return {
        clusterspecs: clusterspecsRes.data.items || [],
        pocketspecs: pocketspecsRes.data.items || [],
        inputspecs: inputspecsRes.data.items || []
      };
    } catch (error) {
      console.error('Failed to fetch specs:', error);
      throw error;
    }
  },

  /**
   * Fetch cluster specifications
   * @returns {Promise<Array>} Array of clusterspec objects
   */
  async fetchClusterspecs() {
    try {
      const response = await api.get(API_ENDPOINTS.CLUSTERSPECS.BASE);
      return response.data.items || [];
    } catch (error) {
      console.error('Failed to fetch clusterspecs:', error);
      throw error;
    }
  },

  /**
   * Fetch pocket specifications
   * @returns {Promise<Array>} Array of pocketspec objects
   */
  async fetchPocketspecs() {
    try {
      const response = await api.get(API_ENDPOINTS.POCKETSPECS.BASE);
      return response.data.items || [];
    } catch (error) {
      console.error('Failed to fetch pocketspecs:', error);
      throw error;
    }
  },

  /**
   * Fetch input specifications
   * @returns {Promise<Array>} Array of inputspec objects
   */
  async fetchInputspecs() {
    try {
      const response = await api.get(API_ENDPOINTS.INPUTSPECS.BASE);
      return response.data.items || [];
    } catch (error) {
      console.error('Failed to fetch inputspecs:', error);
      throw error;
    }
  },

  /**
   * Get inputspec details including YAML content
   * @param {string} specId - The inputspec ID
   * @returns {Promise<Object>} Object containing spec details and yaml content
   */
  async getInputspecDetails(specId) {
    try {
      const [specResponse, yamlResponse] = await Promise.all([
        api.get(API_ENDPOINTS.INPUTSPECS.BY_ID(specId)),
        api.get(API_ENDPOINTS.INPUTSPECS.YAML(specId))
      ]);
      
      return {
        details: specResponse.data,
        yaml: yamlResponse.data
      };
    } catch (error) {
      console.error('Failed to fetch inputspec details:', error);
      throw error;
    }
  },

  /**
   * Find default clusterspec (preferring "6A")
   * @param {Array} clusterspecs - Array of clusterspec objects
   * @returns {Object|null} Default clusterspec or null
   */
  findDefaultClusterspec(clusterspecs) {
    if (!clusterspecs || clusterspecs.length === 0) return null;
    
    // Find "6A" clusterspec or use first one
    return clusterspecs.find(spec => 
      spec.name && spec.name.toLowerCase().includes('6a')
    ) || clusterspecs[0];
  },

  /**
   * Find default pocketspec (preferring "Hydrogen Capping")
   * @param {Array} pocketspecs - Array of pocketspec objects
   * @returns {Object|null} Default pocketspec or null
   */
  findDefaultPocketspec(pocketspecs) {
    if (!pocketspecs || pocketspecs.length === 0) return null;
    
    // Find "Hydrogen Capping" pocketspec or use first one
    return pocketspecs.find(spec => 
      spec.name && spec.name.toLowerCase().includes('hydrogen capping')
    ) || pocketspecs[0];
  },

  /**
   * Find default inputspec (preferring "FMO 6-31G**")
   * @param {Array} inputspecs - Array of inputspec objects
   * @returns {Object|null} Default inputspec or null
   */
  findDefaultInputspec(inputspecs) {
    if (!inputspecs || inputspecs.length === 0) return null;
    
    // Find exactly "FMO 6-31G**" inputspec or use first one
    return inputspecs.find(spec => 
      spec.name && spec.name === 'FMO 6-31G**'
    ) || inputspecs[0];
  }
};